import { type IRootState } from "@/redux/store";
import { languageOptions } from "@/utils/constants/languageList";
import { useSelector, useDispatch } from "react-redux";
import { setLanguage } from "@/redux/slices/LanguageSlice";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Menu, MenuItem, Button, Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export default function Language() {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const langActive = useSelector((state: IRootState) => state.language);

  // Initialize language from localStorage on component mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
      dispatch(setLanguage(savedLanguage));
    } else {
      // Default to "en" if no language is saved
      const defaultLanguage = "en";
      localStorage.setItem("language", defaultLanguage);
      i18n.changeLanguage(defaultLanguage);
      dispatch(setLanguage(defaultLanguage));
    }
  }, [dispatch, i18n]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeLanguage = (language: string): void => {
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);
    dispatch(setLanguage(language));
    handleClose();
  };

  // Get current active language
  const currentLanguage =
    languageOptions.find((lang) => lang.value === langActive.language) ||
    languageOptions[0];

  return (
    <Box>
      <Button
        id="language-button"
        aria-controls={open ? "language-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              textAlign: "center",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
              color: "var(--text-secondary)",
            }}
          />
        }
        sx={{ minWidth: "auto", p: 1 }}
      >
        <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
          {currentLanguage.iconSvg}
        </Box>
      </Button>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "language-button",
        }}
      >
        {languageOptions.map((language) => (
          <MenuItem
            key={language.value}
            onClick={() => handleChangeLanguage(language.value)}
            selected={langActive.language === language.value}
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "var(--text-secondary)",
              color: "var(--text-secondary)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                borderRadius: "50%",
                border:
                  langActive.language === language.value
                    ? "1px solid var(--text-primary)"
                    : "none",
                color: "white",
                "& svg": {
                  fill: "white",
                },
              }}
            >
              {language.iconSvg}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
